import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import PimpinanLayout from "../../components/layout/PimpinanLayout"; // Menggunakan PimpinanLayout
import { useAuth } from "../../context/AuthContext";
import {
  FiArrowLeft, FiUserX, FiRefreshCw, FiSearch,
  FiCalendar, FiCheckCircle, FiAlertCircle, FiUser,
  FiBook, FiClock,
} from "react-icons/fi";

function formatTanggal(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  const bln = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agu","Sep","Okt","Nov","Des"];
  return `${parseInt(d)} ${bln[parseInt(m) - 1]} ${y}`;
}
function jam(str) { return str?.slice(0, 5) ?? "—"; }
function initials(nama) {
  return (nama || "?").split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();
}

const STATUS = {
  kosong   : { label:"Belum Ada Pengganti", dot:"#F59E0B", bg:"#FFFBEB", clr:"#B45309", border:"#FDE68A" },
  sebagian : { label:"Sebagian Terisi",     dot:"#818CF8", bg:"#EEF2FF", clr:"#4F46E5", border:"#C7D2FE" },
  terisi   : { label:"Sudah Ada Pengganti", dot:"#22C55E", bg:"#F0FDF4", clr:"#15803D", border:"#BBF7D0" },
};

export default function LaporanGuruAbsenPimpinan() {
  const { token } = useAuth();
  const navigate  = useNavigate();
  const API       = "https://backend.smpn1kamal.sch.id/api";

  const [laporan,      setLaporan]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState("");
  const [filterStatus, setFilterStatus] = useState("semua");
  const [filterTgl,    setFilterTgl]    = useState("");
  const [expand,       setExpand]       = useState({});

  const fetchLaporan = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterTgl) params.tanggal = filterTgl;
      const res = await axios.get(`${API}/pengganti/laporan-guru`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setLaporan(res.data?.data || []);
    } catch {
      toast.error("Gagal mengambil data laporan guru tidak hadir");
    } finally {
      setLoading(false);
    }
  }, [token, filterTgl]);

  useEffect(() => { fetchLaporan(); }, [fetchLaporan]);

  const filtered = laporan.filter((item) => {
    if (filterStatus !== "semua" && item.status_ringkasan !== filterStatus) return false;
    const q = search.toLowerCase();
    return (
      item.nama_guru?.toLowerCase().includes(q) ||
      item.alasan?.toLowerCase().includes(q) ||
      item.pengganti?.some(p => p.toLowerCase().includes(q))
    );
  });

  const grouped = filtered.reduce((acc, item) => {
    const k = item.tanggal || "—";
    (acc[k] = acc[k] || []).push(item);
    return acc;
  }, {});
  const tglKeys = Object.keys(grouped).sort().reverse();

  const guruUnik      = new Set(laporan.map(i => i.id_guru)).size;
  const totalKosong   = laporan.filter(i => i.status_ringkasan === "kosong").length;
  const totalSebagian = laporan.filter(i => i.status_ringkasan === "sebagian").length;
  const totalTerisi   = laporan.filter(i => i.status_ringkasan === "terisi").length;

  const toggleExpand = (key) =>
    setExpand(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <PimpinanLayout>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        .lga { font-family:'Plus Jakarta Sans',sans-serif; color:#0F172A; }

        /* ── stat grid ── */
        .lga-stats { display:grid; grid-template-columns:repeat(4,1fr); gap:10px; }
        @media(max-width:640px){ .lga-stats { grid-template-columns:repeat(2,1fr); } }

        .lga-stat { background:#fff; border-radius:14px; border:1px solid #F1F5F9;
                    padding:16px 18px; display:flex; align-items:center; gap:12px;
                    box-shadow:0 1px 4px rgba(0,0,0,.04); }
        .lga-si { width:38px; height:38px; border-radius:10px; display:flex;
                  align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
        .lga-sn { font-size:22px; font-weight:800; line-height:1; }
        .lga-sl { font-size:10px; color:#94A3B8; font-weight:600;
                  text-transform:uppercase; letter-spacing:.06em; margin-top:3px; }

        /* ── filter bar ── */
        .lga-filterbar { display:flex; flex-wrap:wrap; gap:10px;
                         align-items:center; justify-content:space-between; margin-bottom:18px; }
        .lga-filterright { display:flex; gap:8px; align-items:center; flex: 1; }

        /* seg buttons */
        .lga-seg { display:flex; border-radius:10px; overflow:hidden;
                   border:1px solid #E2E8F0; background:#F8FAFC; flex-wrap:wrap; }
        .lga-sb  { padding:7px 12px; font-size:12px; font-weight:600; color:#64748B;
                   background:transparent; border:none; cursor:pointer; transition:all .15s;
                   font-family:inherit; white-space:nowrap; }
        .lga-sb.on { background:#4F46E5; color:#fff; }

        /* inputs */
        .lga-iw { position:relative; flex: 1; }
        .lga-iw svg { position:absolute; left:10px; top:50%; transform:translateY(-50%);
                      color:#94A3B8; pointer-events:none; }
        .lga-iw input { padding:8px 12px 8px 32px; font-size:13px; border:1px solid #E2E8F0;
                        border-radius:10px; outline:none; font-family:inherit; color:#0F172A;
                        background:#fff; transition:border .15s; width:100%; box-sizing:border-box; }
        .lga-iw input:focus { border-color:#818CF8; }
        .lga-clr { position:absolute; right:8px; top:50%; transform:translateY(-50%);
                   font-size:11px; color:#94A3B8; cursor:pointer; border:none;
                   background:transparent; line-height:1; }
        .lga-clr:hover { color:#EF4444; }

        /* mobile: filter stack */
        @media(max-width:640px){
          .lga-filterbar { flex-direction:column; align-items:stretch; }
          .lga-filterright { flex-direction:row; width: 100%; }
          .lga-iw input { min-width:0; }
        }

        /* ── date group ── */
        .lga-dg { display:flex; align-items:center; gap:10px; margin:20px 0 10px; }
        .lga-dg-pill { font-size:12px; font-weight:700; color:#4F46E5; background:#EEF2FF;
                       border-radius:8px; padding:4px 12px; white-space:nowrap; }
        .lga-dg-line { flex:1; height:1px; background:#F1F5F9; }
        .lga-dg-cnt  { font-size:12px; color:#94A3B8; font-weight:600; white-space:nowrap; }

        /* ── guru card ── */
        .lga-card { background:#fff; border:1px solid #F1F5F9; border-radius:14px;
                    margin-bottom:8px; overflow:hidden;
                    transition:box-shadow .15s,border-color .15s; }
        .lga-card:hover { box-shadow:0 4px 16px rgba(79,70,229,.07); border-color:#C7D2FE; }

        .lga-ct { padding:16px 20px; display:flex; align-items:flex-start;
                  gap:14px; position:relative; }
        .lga-ct::before { content:''; position:absolute; left:0; top:12px; bottom:12px;
                          width:3px; border-radius:2px; }
        .lga-ct.kosong::before   { background:#F59E0B; }
        .lga-ct.sebagian::before { background:#818CF8; }
        .lga-ct.terisi::before   { background:#22C55E; }

        .lga-av { width:40px; height:40px; border-radius:10px; display:flex;
                  align-items:center; justify-content:center; font-size:14px;
                  font-weight:700; flex-shrink:0; }
        .lga-av.terisi   { background:#F0FDF4; color:#15803D; }
        .lga-av.sebagian { background:#EEF2FF; color:#4F46E5; }
        .lga-av.kosong   { background:#FFFBEB; color:#B45309; }

        .lga-name { font-size:14px; font-weight:700; color:#0F172A; }

        /* nama + badge row — wrap on mobile */
        .lga-namebadge { display:flex; align-items:flex-start;
                         justify-content:space-between; gap:8px; flex-wrap:wrap; }

        .lga-badge { font-size:11px; font-weight:700; padding:4px 10px;
                     border-radius:8px; border:1px solid; flex-shrink:0; white-space:nowrap; }

        .lga-alasan { font-size:12px; color:#94A3B8; font-style:italic;
                      margin-top:5px; padding:5px 10px; background:#F8FAFC;
                      border-radius:7px; border-left:2px solid #E2E8F0; }

        .lga-pbox { display:flex; align-items:center; gap:6px; margin-top:8px;
                    font-size:12px; font-weight:600; padding:7px 12px;
                    border-radius:9px; flex-wrap:wrap; }
        .lga-pbox.ok  { background:#F0FDF4; color:#15803D; border:1px solid #BBF7D0; }
        .lga-pbox.mid { background:#EEF2FF; color:#4338CA; border:1px solid #C7D2FE; }
        .lga-pbox.nok { background:#FFFBEB; color:#B45309; border:1px solid #FDE68A; }

        .lga-exbtn { display:flex; align-items:center; gap:5px; font-size:12px;
                     font-weight:600; color:#4F46E5; background:none; border:none;
                     cursor:pointer; font-family:inherit; padding:0; margin-top:8px; }
        .lga-exbtn:hover { color:#3730A3; }

        /* ── jadwal expand ── */
        .lga-jlist { border-top:1px solid #F1F5F9; background:#FAFAFA; padding:10px 16px 12px; }

        .lga-jrow { display:flex; align-items:center; gap:8px; padding:7px 0;
                    border-bottom:1px solid #F1F5F9; font-size:12px; color:#374151;
                    flex-wrap:wrap; }
        .lga-jrow:last-child { border-bottom:none; }
        .lga-jdot  { width:7px; height:7px; border-radius:50%; flex-shrink:0; }
        .lga-jbadge { font-size:10px; font-weight:700; padding:2px 7px;
                      border-radius:6px; border:1px solid; margin-left:auto; white-space:nowrap; }

        /* on mobile hide separator dots, stack rows */
        @media(max-width:480px){
          .lga-ct { padding:14px 14px 14px 18px; gap:10px; }
          .lga-jrow { gap:6px; }
          .lga-jbadge { margin-left:0; }
        }

        /* ── states ── */
        .lga-state { display:flex; flex-direction:column; align-items:center;
                     padding:56px 0; gap:8px; color:#CBD5E1; }
        .lga-state p    { color:#64748B; font-size:14px; font-weight:600; margin:0; }
        .lga-state span { font-size:12px; color:#94A3B8; }
        .lga-spin { width:30px; height:30px; border-radius:50%;
                    border:3px solid #E2E8F0; border-top-color:#4F46E5;
                    animation:lgaspin .7s linear infinite; }
        @keyframes lgaspin { to { transform:rotate(360deg); } }

        /* ── header buttons ── */
        .lga-rfbtn { display:flex; align-items:center; gap:7px; font-size:13px; font-weight:600;
                     color:#64748B; border:1px solid #E2E8F0; border-radius:10px; padding:8px 16px;
                     background:#fff; cursor:pointer; transition:all .15s; font-family:inherit; }
        .lga-rfbtn:hover { color:#4F46E5; border-color:#818AF8; background:#F8F7FF; }

        /* ── panel ── */
        .lga-panel { background:#fff; border-radius:20px; border:1px solid #F1F5F9;
                     box-shadow:0 2px 12px rgba(0,0,0,.05); padding:20px; }
        @media(max-width:480px){ .lga-panel { padding:14px; border-radius:14px; } }

        .lga-foot { font-size:12px; color:#94A3B8; text-align:right;
                    padding-top:12px; border-top:1px solid #F1F5F9; margin-top:4px; }
      `}</style>

      <div className="lga space-y-5">

        {/* ── HEADER ── */}
        <div style={{ display:"flex", flexWrap:"wrap", alignItems:"flex-start",
                      justifyContent:"space-between", gap:10 }}>
          <div>
            <h1 style={{ fontSize:20, fontWeight:800, color:"#0F172A",
                         display:"flex", alignItems:"center", gap:8, margin:0 }}>
              <span style={{ color:"#EF4444" }}><FiUserX /></span>
              Laporan Guru Tidak Hadir
            </h1>
            <p style={{ fontSize:13, color:"#94A3B8", marginTop:3, marginBottom:0 }}>
              Monitoring Pimpinan · SMPN 1 Kamal
            </p>
          </div>
          {/* Mengarahkan tombol kembali ke dashboard pimpinan */}
          <button className="lga-rfbtn" onClick={() => navigate("/pimpinan")}>
            <FiArrowLeft size={13}/> Kembali
          </button>
        </div>

        {/* ── STAT ── */}
        <div className="lga-stats">
          {[
            { icon:<FiUserX />,       bg:"#EFF6FF", clr:"#3B82F6", num:guruUnik,       lbl:"Guru Tidak Hadir"   },
            { icon:<FiAlertCircle />, bg:"#FFFBEB", clr:"#F59E0B", num:totalKosong,   lbl:"Belum Ada Pengganti"},
            { icon:<FiUser />,        bg:"#EEF2FF", clr:"#4F46E5", num:totalSebagian, lbl:"Sebagian Terisi"    },
            { icon:<FiCheckCircle />, bg:"#F0FDF4", clr:"#22C55E", num:totalTerisi,   lbl:"Semua Terisi"       },
          ].map((s, i) => (
            <div key={i} className="lga-stat">
              <div className="lga-si" style={{ background:s.bg, color:s.clr }}>{s.icon}</div>
              <div>
                <div className="lga-sn" style={{ color:i===0?"#0F172A":s.clr }}>{s.num}</div>
                <div className="lga-sl">{s.lbl}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ── PANEL ── */}
        <div className="lga-panel">

          {/* filter bar */}
          <div className="lga-filterbar">
            <div className="lga-seg">
              {[
                { v:"semua",    l:"Semua"        },
                { v:"kosong",   l:"Belum Terisi" },
                { v:"sebagian", l:"Sebagian"     },
                { v:"terisi",   l:"Sudah Terisi" },
              ].map(o => (
                <button key={o.v}
                  className={`lga-sb${filterStatus===o.v?" on":""}`}
                  onClick={() => setFilterStatus(o.v)}
                >{o.l}</button>
              ))}
            </div>

            <div className="lga-filterright">
              <div className="lga-iw">
                <FiCalendar size={12}/>
                <input type="date" value={filterTgl}
                  onChange={e => setFilterTgl(e.target.value)}/>
                {filterTgl && (
                  <button className="lga-clr" onClick={() => setFilterTgl("")}>✕</button>
                )}
              </div>
              <div className="lga-iw">
                <FiSearch size={12}/>
                <input type="text" placeholder="Cari guru atau pengganti…"
                  value={search} onChange={e => setSearch(e.target.value)}/>
              </div>
            </div>
          </div>

          {/* loading */}
          {loading ? (
            <div className="lga-state">
              <div className="lga-spin"/>
              <p>Memuat data…</p>
            </div>

          /* empty */
          ) : filtered.length === 0 ? (
            <div className="lga-state">
              <FiUserX size={36}/>
              <p>Tidak ada data ditemukan</p>
              <span>Coba ubah filter atau kata pencarian</span>
            </div>

          ) : (
            <>
              {tglKeys.map(tgl => (
                <div key={tgl}>

                  {/* tanggal header */}
                  <div className="lga-dg">
                    <span className="lga-dg-pill">
                      <FiCalendar style={{ display:"inline", marginRight:4, verticalAlign:-2 }} size={11}/>
                      {formatTanggal(tgl)}
                    </span>
                    <div className="lga-dg-line"/>
                    <span className="lga-dg-cnt">{grouped[tgl].length} guru tidak hadir</span>
                  </div>

                  {grouped[tgl].map((item) => {
                    const key  = `${item.id_guru}_${item.tanggal}`;
                    const st   = STATUS[item.status_ringkasan] || STATUS.kosong;
                    const open = expand[key];

                    return (
                      <div key={key} className="lga-card">

                        {/* card top */}
                        <div className={`lga-ct ${item.status_ringkasan}`}>
                          <div className={`lga-av ${item.status_ringkasan}`}>
                            {initials(item.nama_guru)}
                          </div>

                          <div style={{ flex:1, minWidth:0 }}>
                            {/* nama + badge */}
                            <div className="lga-namebadge">
                              <span className="lga-name">{item.nama_guru}</span>
                              <span className="lga-badge"
                                style={{ background:st.bg, color:st.clr, borderColor:st.border }}>
                                <span style={{ display:"inline-block", width:6, height:6,
                                               borderRadius:"50%", background:st.dot,
                                               marginRight:5, verticalAlign:1 }}/>
                                {st.label}
                              </span>
                            </div>

                            {/* alasan */}
                            {item.alasan && (
                              <div className="lga-alasan">"{item.alasan}"</div>
                            )}

                            {/* info pengganti */}
                            {item.status_ringkasan === "terisi" ? (
                              <div className="lga-pbox ok">
                                <FiCheckCircle size={12}/>
                                Digantikan oleh:&nbsp;<strong>{item.pengganti.join(", ")}</strong>
                              </div>
                            ) : item.status_ringkasan === "sebagian" ? (
                              <div className="lga-pbox mid">
                                <FiUser size={12}/>
                                Pengganti:&nbsp;<strong>{item.pengganti.join(", ")}</strong>
                                &nbsp;·&nbsp;{item.total_terisi}/{item.total_jadwal} jadwal terisi
                              </div>
                            ) : (
                              <div className="lga-pbox nok">
                                <FiAlertCircle size={12}/>
                                Belum ada guru yang mengambil jadwal ini
                              </div>
                            )}

                            {/* toggle detail */}
                            {item.jadwal?.length > 0 && (
                              <button className="lga-exbtn" onClick={() => toggleExpand(key)}>
                                <FiBook size={11}/>
                                {open ? "Sembunyikan" : "Lihat"} detail jadwal ({item.total_jadwal})
                              </button>
                            )}
                          </div>
                        </div>

                        {/* expand: detail per jadwal */}
                        {open && (
                          <div className="lga-jlist">
                            {item.jadwal.map((j, ji) => {
                              const jst = j.status === "diambil"
                                ? { dot:"#22C55E", bg:"#F0FDF4", clr:"#15803D", border:"#BBF7D0", lbl:"Terisi" }
                                : { dot:"#F59E0B", bg:"#FFFBEB", clr:"#B45309", border:"#FDE68A", lbl:"Kosong" };
                              return (
                                <div key={ji} className="lga-jrow">
                                  <span className="lga-jdot" style={{ background:jst.dot }}/>
                                  <span style={{ fontWeight:600, color:"#1E293B", whiteSpace:"nowrap" }}>
                                    {j.nama_kelas}
                                  </span>
                                  <span style={{ color:"#94A3B8" }}>·</span>
                                  <span style={{ color:"#64748B" }}>{j.nama_mapel}</span>
                                  <span style={{ display:"flex", alignItems:"center", gap:3,
                                                 color:"#94A3B8", fontSize:11, whiteSpace:"nowrap" }}>
                                    <FiClock size={10}/>{jam(j.jam_mulai)}–{jam(j.jam_selesai)}
                                  </span>
                                  {j.nama_pengganti && (
                                    <span style={{ display:"flex", alignItems:"center", gap:3,
                                                   fontSize:11, color:"#15803D" }}>
                                      <FiUser size={10}/>{j.nama_pengganti}
                                    </span>
                                  )}
                                  <span className="lga-jbadge"
                                    style={{ background:jst.bg, color:jst.clr, borderColor:jst.border }}>
                                    {jst.lbl}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </>
          )}

          {!loading && filtered.length > 0 && (
            <div className="lga-foot">
              Menampilkan {filtered.length} dari {laporan.length} laporan
            </div>
          )}
        </div>
      </div>
    </PimpinanLayout>
  );
}